import ProductImage from './ProductImage';
import React, { useState, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useProductCard } from '../hooks/useProductCard';
import useDialogFocus from '../hooks/useDialogFocus';

const ProductCardMobile = ({ product }) => {
  const {
    notice,
    currentIndex,
    setCurrentIndex,
    fullUrls,
    modalOpen,
    variants,
    selectedVariant,
    setSelectedVariant,
    loadingVariants,
    addToCart,
    closeModal,
    handleModalAdd,
    toggleFavorite,
    favorite,
    displayPrice,
    oldPrice,
    discountPercent,
  } = useProductCard(product);
  const dialogRef = useDialogFocus(modalOpen, closeModal);

  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const containerRef = useRef(null);
  const isSwiping = useRef(false);
  const location = useLocation();

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    setIsDragging(true);
    setDragOffset(0);
    isSwiping.current = false;
  }, []);

  const handleTouchMove = useCallback(
    (e) => {
      if (!isDragging) return;
      const deltaX = e.touches[0].clientX - touchStartX.current;
      const deltaY = e.touches[0].clientY - touchStartY.current;

      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        e.preventDefault();
        isSwiping.current = true;
        const containerWidth = containerRef.current?.offsetWidth || 1;
        const maxOffset = containerWidth * 0.5;
        const clampedDelta = Math.max(-maxOffset, Math.min(maxOffset, deltaX));
        setDragOffset(clampedDelta);
      } else {
        setIsDragging(false);
        setDragOffset(0);
      }
    },
    [isDragging]
  );

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    const threshold = (containerRef.current?.offsetWidth || 300) * 0.3;

    if (dragOffset < -threshold && currentIndex < fullUrls.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else if (dragOffset > threshold && currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
    setDragOffset(0);
  }, [isDragging, dragOffset, currentIndex, fullUrls.length, setCurrentIndex]);

  const handleCardClick = (e) => {
    if (isSwiping.current) {
      e.preventDefault();
      e.stopPropagation();
      isSwiping.current = false;
      return;
    }
    sessionStorage.setItem(
      'catalogScroll',
      JSON.stringify({
        url: location.pathname + location.search,
        scroll: window.scrollY,
      })
    );
  };

  const rating = product.rating || 0;
  const reviewsCount = product.reviews_count || 0;
  const translateX =
    -currentIndex * 100 +
    (dragOffset / (containerRef.current?.offsetWidth || 1)) * 100;

  return (
    <>
      <article
        className="product-card"
        style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
      >
        <button
          onClick={toggleFavorite}
          className={`favorite-btn ${favorite ? 'active' : ''}`}
          aria-label={
            favorite ? 'Убрать из избранного' : 'Добавить в избранное'
          }
          type="button"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill={favorite ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        </button>

        <Link
          to={`/product/${product.id}`}
          onClick={handleCardClick}
          aria-label={product.title}
        >
          <div
            className="product-card-img-wrapper"
            ref={containerRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="slider-track"
              style={{
                transform: `translateX(${translateX}%)`,
                transition: isDragging ? 'none' : 'transform 0.3s ease',
              }}
            >
              {fullUrls.map((url, idx) => (
                <ProductImage key={idx} product={product} src={url} alt={`${product.title} ${idx + 1}`} width="100%" height="100%" style={{ objectFit: 'cover', flexShrink: 0 }} />
              ))}
            </div>
            {discountPercent > 0 && (
              <span className="discount-badge-on-image">
                -{discountPercent}%
              </span>
            )}
            {/* Блок склеек удалён */}
          </div>
        </Link>

        <div className="product-card-body">
          <div className="price-block">
            <span className="product-card-price">{displayPrice} ₽</span>
            {oldPrice && <span className="old-price">{oldPrice} ₽</span>}
          </div>
          <h3 className="product-card-title">
            <Link to={`/product/${product.id}`} onClick={handleCardClick}>
              {product.title}
            </Link>
          </h3>
          {rating > 0 && (
            <div className="product-card-rating">
              <span className="star-icon">★</span>
              <span>{rating.toFixed(1)}</span>
              <span className="reviews-count">({reviewsCount})</span>
            </div>
          )}
          {notice && <p className="purchase-notice" role="status">{notice}</p>}
          <div className="product-card-actions">
            <button className="btn btn-primary" onClick={addToCart}>
              Выбрать размер
            </button>
          </div>
        </div>
      </article>

      {modalOpen && (
        <div className="modal-overlay">
          <button
            className="modal-backdrop"
            aria-label="Закрыть выбор размера"
            tabIndex={-1}
            onClick={closeModal}
          />
          <div
            ref={dialogRef}
            className="modal-content"
            role="dialog"
            aria-modal="true"
            aria-label="Выберите размер товара"
            tabIndex={-1}
          >
            <button
              className="modal-close"
              onClick={closeModal}
              aria-label="Закрыть выбор размера"
            >
              ✕
            </button>
            <h3>Выберите размер</h3>
            {loadingVariants ? (
              <p>Загрузка размеров...</p>
            ) : (
              <>
                <div className="variant-list">
                  {variants.map((v) => {
                    const isAvailable = v.stock > 0;
                    const isSelected = selectedVariant?.id === v.id;
                    const variantDiscount = oldPrice
                      ? Math.round((1 - (v.retailPrice ?? 0) / oldPrice) * 100)
                      : null;
                    return (
                      <button
                        key={v.id}
                        className={`variant-btn ${isSelected ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}`}
                        disabled={!isAvailable}
                        onClick={() => setSelectedVariant(v)}
                      >
                        <span>{v.size}</span>
                        <span className="variant-price">
                          {v.retailPrice ?? ''} ₽
                        </span>
                        {variantDiscount > 0 && (
                          <span className="variant-discount">
                            -{variantDiscount}%
                          </span>
                        )}
                        {!isAvailable && (
                          <span className="out-of-stock">(нет)</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <button
                  className="btn btn-primary"
                  style={{ width: '100%', marginTop: 15 }}
                  disabled={!selectedVariant || selectedVariant.stock === 0}
                  onClick={handleModalAdd}
                >
                  В корзину
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCardMobile;
